import React, { useState, useRef, useEffect } from "react";
import { askClaude, generateReport, comparePlatforms } from "../services/api";

const QUICK_CHIPS = [
  "Which campaign should I scale?",
  "Analyze my ROAS performance",
  "Generate a weekly report",
  "Compare Google vs Meta",
  "Where am I wasting budget?",
  "Give me ad copy suggestions",
];

export default function AIInsights() {
  const [messages, setMessages]     = useState([
    {
      role: "ai",
      text: "👋 Hello! I'm connected to your Google Ads and Meta Ads accounts.\n\nI can analyze your performance, generate reports, suggest optimizations, and compare platforms. Try one of the quick prompts below or ask me anything!",
    },
  ]);
  const [input, setInput]           = useState("");
  const [loading, setLoading]       = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);
  const [budget, setBudget]         = useState("");
  const messagesEndRef               = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const addMessage = (role, text) =>
    setMessages((prev) => [...prev, { role, text }]);

  const send = async (question) => {
    const q = (question || input).trim();
    if (!q || loading) return;
    setInput("");
    addMessage("user", q);
    setLoading(true);
    // placeholder
    setMessages((prev) => [...prev, { role: "ai", text: "__loading__" }]);
    try {
      const res = await askClaude(q);
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "ai", text: res.answer };
        return copy;
      });
    } catch (e) {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "ai", text: "⚠️ Error: " + e.message };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReport = async () => {
    setReportLoading(true);
    addMessage("user", "Generate a full performance report for the last 30 days");
    setMessages((prev) => [...prev, { role: "ai", text: "__loading__" }]);
    try {
      const res = await generateReport("last 30 days");
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "ai", text: res.report };
        return copy;
      });
    } catch (e) {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "ai", text: "⚠️ Error: " + e.message };
        return copy;
      });
    } finally {
      setReportLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!budget.trim()) { alert("Please enter a total budget first"); return; }
    setCompareLoading(true);
    addMessage("user", `Compare Google vs Meta for a total budget of ${budget}`);
    setMessages((prev) => [...prev, { role: "ai", text: "__loading__" }]);
    try {
      const res = await comparePlatforms(budget);
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "ai", text: res.analysis };
        return copy;
      });
    } catch (e) {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "ai", text: "⚠️ Error: " + e.message };
        return copy;
      });
    } finally {
      setCompareLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Claude AI Insights</div>
          <div className="page-subtitle">Powered by your live ad account data</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            className="chat-input"
            style={{ width: 140 }}
            placeholder="Total budget (e.g. ₹50,000)"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
          />
          <button className="btn btn-ghost" onClick={handleCompare} disabled={compareLoading}>
            {compareLoading ? "Comparing…" : "⇄ Compare Platforms"}
          </button>
          <button className="btn btn-primary" onClick={handleReport} disabled={reportLoading}>
            {reportLoading ? "Generating…" : "⬇ Auto Report"}
          </button>
        </div>
      </div>

      <div className="page-body">
        <div className="quick-chips">
          {QUICK_CHIPS.map((chip) => (
            <button key={chip} className="chip" onClick={() => send(chip)}>
              {chip}
            </button>
          ))}
        </div>

        <div className="chat-wrap">
          <div className="chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`msg ${msg.role === "user" ? "user" : ""}`}>
                <div className={`msg-avatar ${msg.role === "ai" ? "ai" : "user"}`}>
                  {msg.role === "ai" ? "C" : "U"}
                </div>
                <div className="msg-body">
                  {msg.text === "__loading__" ? (
                    <div className="dots"><span /><span /><span /></div>
                  ) : (
                    msg.text
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-row">
            <input
              className="chat-input"
              placeholder="Ask Claude about your campaigns…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              disabled={loading}
            />
            <button className="btn btn-primary" onClick={() => send()} disabled={loading || !input.trim()}>
              {loading ? "…" : "Send ↗"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
